import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import CloseButton from 'components/shared/CloseButton';
import Search from 'components/shared/Search';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  adminIndexMappings,
  adminIndexReceivedValues,
  adminMappingsQueryKeys,
  DONT_MAP,
  MappingEntry,
  MappingType,
  ReceivedValue,
  WILDCARD,
} from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';

import AgentSettingsModal from './AgentSettingsModal';
import { Chip, Muted } from './Chips';
import MapPanel, { MapPanelResult } from './MapPanel';
import { FallbackRule } from './MappingSettings';
import MappingsTable, { MappingRow } from './MappingsTable';
import NewValues from './NewValues';
import {
  hasInvalidThreshold,
  keepMine,
  normalizeKey,
  PendingOperation,
  removeSlot,
  TYPE_TITLE,
  upsertOperation,
} from './operations';

type Props = {
  configId: string;
  clientId: string;
  ehr: boolean;
  type: MappingType;
  disabled: boolean;
  operations: PendingOperation[];
  onApply: (operations: PendingOperation[]) => void;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
};

export default function MappingsSlideout({
  configId,
  clientId,
  ehr,
  type,
  disabled,
  operations,
  onApply,
  onClose,
  onDirtyChange,
}: Props) {
  const [draft, setDraft] = useState<PendingOperation[]>(operations);
  const [panelKey, setPanelKey] = useState<string | null | undefined>(undefined);
  const [panelMode, setPanelMode] = useState<'map' | 'dont_map'>('map');
  const [search, setSearch] = useState('');
  const [accepting, setAccepting] = useState<ReceivedValue | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  const recordNoun = ehr ? 'record' : 'patient';
  const { profile } = useProfile();
  const canAddToNetwork = profile.has(PERMISSIONS.adminClientEdit);

  const mappings = useQuery({
    queryKey: adminMappingsQueryKeys.entries(configId, type),
    queryFn: () => adminIndexMappings(configId, type),
  });
  const networkAddable = mappings.data?.networkAddable ?? true;
  const receivedValues = useQuery({
    queryKey: adminMappingsQueryKeys.receivedValues(configId, type, 'all'),
    queryFn: () => adminIndexReceivedValues(configId, type, 'all'),
  });
  const newValues = useQuery({
    queryKey: adminMappingsQueryKeys.receivedValues(configId, type, 'new'),
    queryFn: () => adminIndexReceivedValues(configId, type, 'new'),
  });

  const typeOps = useMemo(() => draft.filter((o) => o.type === type), [draft, type]);
  const values = useMemo(() => (receivedValues.data ?? []).filter((v) => !v.dismissed), [receivedValues.data]);

  const entries = useMemo(() => {
    const map = new Map<string, MappingEntry>();
    (mappings.data?.data ?? []).forEach((e) => {
      if (e.key !== WILDCARD) map.set(normalizeKey(e.key), e);
    });
    return map;
  }, [mappings.data]);

  const valuesByKey = useMemo(() => {
    const map = new Map<string, ReceivedValue>();
    values.forEach((v) => map.set(normalizeKey(v.name), v));
    return map;
  }, [values]);

  const rows = useMemo(() => {
    const byKey = new Map<string, MappingRow>();
    entries.forEach((entry, k) => {
      byKey.set(k, { key: entry.key, entry, pending: null, value: valuesByKey.get(k) ?? null });
    });
    typeOps.forEach((op) => {
      if (!('key' in op) || op.op === 'dismiss') return;
      const k = normalizeKey(op.key);
      const row = byKey.get(k) ?? { key: op.key, entry: null, pending: null, value: valuesByKey.get(k) ?? null };
      byKey.set(k, { ...row, pending: op });
    });
    const query = search.trim().toLowerCase();
    const all = [...byKey.values()];
    if (!query) return all;
    return all.filter(
      (r) => r.key.toLowerCase().includes(query) || (r.entry?.resolved?.name ?? '').toLowerCase().includes(query)
    );
  }, [entries, typeOps, valuesByKey, search]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(operations), [draft, operations]);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const stage = useCallback((op: PendingOperation) => setDraft((ops) => upsertOperation(ops, op)), []);
  const undo = useCallback((slot: string) => setDraft((ops) => removeSlot(ops, slot)), []);
  const resolveConflict = useCallback((op: PendingOperation) => stage(keepMine(op)), [stage]);

  const storedTarget = useCallback((key: string) => entries.get(normalizeKey(key))?.target ?? null, [entries]);
  const storedKey = useCallback((key: string) => entries.get(normalizeKey(key))?.key ?? key, [entries]);

  const stageDontMap = useCallback(
    (key: string) => stage({ op: 'dont_map', type, key: storedKey(key), expected: storedTarget(key) }),
    [stage, type, storedKey, storedTarget]
  );

  const openPanel = (key: string | null, mode: 'map' | 'dont_map' = 'map') => {
    setPanelMode(mode);
    setPanelKey(key);
  };

  const dontMap = (key: string) => (ehr && type === 'groups' ? openPanel(key, 'dont_map') : stageDontMap(key));

  const onPanelSave = ({ key, target, label, addToNetwork }: MapPanelResult) => {
    if (target === DONT_MAP) {
      stageDontMap(key);
    } else {
      stage({
        op: 'set',
        type,
        key: storedKey(key),
        target,
        expected: storedTarget(key),
        label,
        ...(addToNetwork ? { addToNetwork: true } : {}),
      });
    }
    setPanelKey(undefined);
  };

  const stageAccept = (value: ReceivedValue, addToNetwork: boolean) => {
    stage({
      op: 'accept_suggestion',
      type,
      key: storedKey(value.name),
      expected: storedTarget(value.name),
      expectedTarget: value.suggestion?.agentTarget ?? null,
      label: value.suggestion?.target?.name ?? null,
      ...(addToNetwork ? { addToNetwork: true } : {}),
    });
    setAccepting(null);
  };

  const onAccept = (value: ReceivedValue) => {
    if (value.suggestion?.inNetwork === false) setAccepting(value);
    else stageAccept(value, false);
  };

  const settingsOp = typeOps.find((o) => o.op === 'settings');
  const instructionsOp = typeOps.find((o) => o.op === 'instructions');
  const fallbackOp = typeOps.find((o) => o.op === 'fallback');
  const pendingCount = typeOps.length;
  const agentSettingsPending = !!settingsOp || !!instructionsOp;
  const agentSettingsProblem = !!(instructionsOp?.error || instructionsOp?.conflict);

  const updateSettings = (patch: { threshold?: number | null; newHospitalThreshold?: number | null }) => {
    const agent = mappings.data?.agent;
    if (!agent) return;
    const current = settingsOp?.op === 'settings' ? settingsOp : null;
    const storedNew = agent.newHospitals?.threshold;
    const threshold =
      patch.threshold !== undefined ? (patch.threshold ?? agent.threshold) : (current?.threshold ?? agent.threshold);
    const newHospitalThreshold =
      patch.newHospitalThreshold !== undefined
        ? (patch.newHospitalThreshold ?? storedNew)
        : (current?.newHospitalThreshold ?? storedNew);
    const thresholdChanged = threshold !== agent.threshold;
    const newHospitalChanged = !!agent.newHospitals && newHospitalThreshold !== storedNew;
    if (!thresholdChanged && !newHospitalChanged) {
      undo(`${type}|settings`);
      return;
    }
    stage({
      op: 'settings',
      type,
      ...(thresholdChanged ? { threshold } : {}),
      ...(newHospitalChanged ? { newHospitalThreshold } : {}),
    });
  };
  const loading = mappings.isLoading || receivedValues.isLoading || newValues.isLoading;

  return (
    <PanelContainer>
      <PanelHeader>
        <TitleRow>
          <PanelTitle>{TYPE_TITLE[type]}</PanelTitle>
          {pendingCount > 0 && <Chip $tone='amber'>{pendingCount} pending</Chip>}
        </TitleRow>
        <HeaderActions>
          <Button variant='outline-primary' size='small' onClick={() => setSettingsOpen(true)}>
            Auto-mapping agent settings
            {agentSettingsProblem ? (
              <Chip $tone='red'>Problem</Chip>
            ) : (
              agentSettingsPending && <Chip $tone='amber'>Pending</Chip>
            )}
          </Button>
          <CloseButton onClick={onClose} />
        </HeaderActions>
      </PanelHeader>

      <PanelContent>
        {loading ? (
          <CircularProgress />
        ) : mappings.isError ? (
          <Muted>These mappings couldn&apos;t be loaded. Close and try again.</Muted>
        ) : accepting ? (
          <ConfirmPanel>
            <PanelTitle>{networkAddable ? 'Add to network?' : 'Not in this network'}</PanelTitle>
            <Muted>
              {accepting.suggestion?.target?.name ?? 'This group'} isn&apos;t in this client&apos;s network.{' '}
              {!networkAddable
                ? "This client's network comes from the clients that include it, so a group can't be added here. Use Map to pick one already in its network."
                : canAddToNetwork
                  ? "It's added when you submit, and stays in the network if the mapping changes later."
                  : "Adding a group to a client's network needs client edit permission, so someone with it has to accept this one."}
            </Muted>
            <ButtonGroup>
              <Button variant='ghost' onClick={() => setAccepting(null)}>
                Back
              </Button>
              {networkAddable && (
                <Button onClick={() => stageAccept(accepting, true)} disabled={!canAddToNetwork}>
                  Add to network and accept
                </Button>
              )}
            </ButtonGroup>
          </ConfirmPanel>
        ) : panelKey !== undefined ? (
          <MapPanel
            configId={configId}
            clientId={clientId}
            ehr={ehr}
            type={type}
            mappingKey={panelKey}
            networkAddable={networkAddable}
            currentLabel={panelKey ? (entries.get(normalizeKey(panelKey))?.resolved?.name ?? null) : null}
            initialMode={panelMode}
            onCancel={() => setPanelKey(undefined)}
            onSave={onPanelSave}
          />
        ) : (
          <>
            <NewValues
              type={type}
              fresh={newValues.data ?? []}
              entries={entries}
              operations={typeOps}
              recordNoun={recordNoun}
              disabled={disabled}
              onAcknowledge={(entry) =>
                stage({ op: 'acknowledge', type, key: entry.key, expected: entry.target ?? null })
              }
              onMap={(key) => openPanel(key)}
              onDontMap={(key) => dontMap(key)}
              onDismiss={(value) => stage({ op: 'dismiss', type, key: value.name, attrValueId: value.id })}
              onAccept={onAccept}
              onReject={(value) =>
                stage({
                  op: 'reject_suggestion',
                  type,
                  key: storedKey(value.name),
                  expectedTarget: value.suggestion?.agentTarget ?? null,
                })
              }
              onUndo={undo}
              onKeepMine={resolveConflict}
            />

            <FallbackRule
              stored={mappings.data?.fallback ?? null}
              pending={fallbackOp?.op === 'fallback' ? fallbackOp.target : undefined}
              problem={fallbackOp}
              disabled={disabled}
              onChange={(target) =>
                target === undefined
                  ? undo(`${type}|fallback`)
                  : stage({ op: 'fallback', type, target, expected: mappings.data?.fallback ?? null })
              }
              onKeepMine={resolveConflict}
              onUndo={undo}
            />

            <SearchBar>
              <Search placeholder='Search mappings' value={search} onChange={setSearch} />
              {!disabled && <Button onClick={() => openPanel(null)}>Add mapping</Button>}
            </SearchBar>
            <MappingsTable
              type={type}
              rows={rows}
              recordNoun={recordNoun}
              disabled={disabled}
              onChange={(row) => openPanel(row.key)}
              onDontMap={(row) => dontMap(row.key)}
              onAcknowledge={(row) =>
                row.entry && stage({ op: 'acknowledge', type, key: row.entry.key, expected: row.entry.target ?? null })
              }
              onRemove={(row) => stage({ op: 'remove', type, key: row.key, expected: row.entry?.target ?? null })}
              onUndo={undo}
              onKeepMine={resolveConflict}
            />
          </>
        )}
      </PanelContent>

      {settingsOpen && (
        <AgentSettingsModal
          type={type}
          ehr={ehr}
          agent={mappings.data?.agent}
          instructions={mappings.data?.instructions ?? null}
          settingsOp={settingsOp}
          instructionsOp={instructionsOp}
          disabled={disabled}
          onSettingsChange={updateSettings}
          onInstructionsChange={(text) =>
            text === null ? undo(`${type}|instructions`) : stage({ op: 'instructions', type, text })
          }
          onUndo={undo}
          onClose={closeSettings}
        />
      )}

      <PanelFooter>
        <Muted>Changes stay pending until you submit.</Muted>
        <ButtonGroup>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(draft);
            }}
            disabled={disabled || panelKey !== undefined || accepting !== null || hasInvalidThreshold(draft)}>
            Apply
          </Button>
        </ButtonGroup>
      </PanelFooter>
    </PanelContainer>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConfirmPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;
